import { memo, type FC } from 'react';
import { Link } from 'react-router-dom';
import { Star } from 'lucide-react';
import type { BookingResponse } from '../../types/booking.types';
import { formatCurrencyAmount } from '../../utils/currency';
import { formatBookingDate, formatBookingTime } from './bookingUtils';

export interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'md';
}

const statusStyles: Record<string, string> = {
  CONFIRMED: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300',
  PENDING_PAYMENT: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
  COMPLETED: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
  CANCELLED: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  NO_SHOW: 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-300',
};

export const StatusBadge: FC<StatusBadgeProps> = ({ status, size = 'sm' }) => {
  const sizeClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  return (
    <span className={`${sizeClasses} font-medium rounded-full ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.replace('_', ' ')}
    </span>
  );
};

export interface BookingCardProps {
  booking: BookingResponse;
  onCancel?: (id: string) => void;
  onReschedule?: (booking: BookingResponse) => void;
  onReview?: (booking: BookingResponse) => void;
  hasReview?: boolean;
  isCancelling?: boolean;
  showActions?: boolean;
  variant?: 'default' | 'compact';
}

const BookingCardComponent: FC<BookingCardProps> = ({
  booking, onCancel, onReschedule, onReview, hasReview = false,
  isCancelling = false, showActions = true, variant = 'default',
}) => {
  const isUpcoming = new Date(booking.startTime) > new Date();
  const canCancel = showActions && isUpcoming && booking.status !== 'CANCELLED'
    && booking.status !== 'COMPLETED' && onCancel;
  const canReschedule = showActions && isUpcoming && booking.status === 'CONFIRMED' && onReschedule;
  const canReview = showActions && !isUpcoming && booking.status === 'COMPLETED' && !hasReview && onReview;

  if (variant === 'compact') {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-3 hover:shadow-sm dark:hover:shadow-slate-900/50 transition-shadow">
        <div className="flex justify-between items-start mb-2">
          <h4 className="font-medium text-gray-900 dark:text-slate-100 text-sm truncate">{booking.service?.name || 'Service'}</h4>
          <StatusBadge status={booking.status} size="sm" />
        </div>
        <p className="text-xs text-gray-600 dark:text-slate-400">
          {formatBookingDate(booking.startTime)} at {formatBookingTime(booking.startTime)}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm dark:shadow-slate-900/50 border border-gray-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-slate-100">{booking.service?.name || 'Service'}</h3>
          <p className="text-sm text-gray-600 dark:text-slate-400">with {booking.therapist?.user?.name || 'Therapist'}</p>
        </div>
        <StatusBadge status={booking.status} />
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm mb-4">
        <div>
          <span className="text-gray-500 dark:text-slate-400">Date:</span>
          <span className="ml-1 text-gray-900 dark:text-slate-100">{formatBookingDate(booking.startTime)}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-slate-400">Time:</span>
          <span className="ml-1 text-gray-900 dark:text-slate-100">{formatBookingTime(booking.startTime)}</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-slate-400">Duration:</span>
          <span className="ml-1 text-gray-900 dark:text-slate-100">{booking.service?.duration || 60} min</span>
        </div>
        <div>
          <span className="text-gray-500 dark:text-slate-400">Total:</span>
          <span className="ml-1 text-gray-900 dark:text-slate-100 font-medium">{formatCurrencyAmount(Number(booking.totalAmount))}</span>
        </div>
      </div>
      {booking.customerNotes && (
        <p className="text-sm text-gray-600 dark:text-slate-400 italic mb-3">"{booking.customerNotes}"</p>
      )}
      {showActions && (
        <div className="flex gap-2">
          <Link
            to={`/bookings/${booking.id}`}
            className="flex-1 text-center px-3 py-2 text-sm text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30"
          >
            View Details
          </Link>
          {canReschedule && (
            <button
              onClick={() => onReschedule(booking)}
              className="px-3 py-2 text-sm text-blue-600 dark:text-blue-400 border border-blue-300 dark:border-blue-700 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30"
            >
              Reschedule
            </button>
          )}
          {canCancel && (
            <button
              onClick={() => onCancel(booking.id)}
              disabled={isCancelling}
              className="px-3 py-2 text-sm text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
            >
              {isCancelling ? 'Cancelling...' : 'Cancel'}
            </button>
          )}
          {canReview && (
            <button
              onClick={() => onReview(booking)}
              className="flex items-center gap-1 px-3 py-2 text-sm text-yellow-600 dark:text-yellow-400 border border-yellow-300 dark:border-yellow-700 rounded-md hover:bg-yellow-50 dark:hover:bg-yellow-900/30"
            >
              <Star className="w-4 h-4" />
              Leave Review
            </button>
          )}
          {hasReview && booking.status === 'COMPLETED' && (
            <span className="flex items-center gap-1 px-3 py-2 text-sm text-green-600 dark:text-green-400">
              <Star className="w-4 h-4 fill-current" />
              Reviewed
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Memoize to prevent unnecessary re-renders in booking lists
export const BookingCard = memo(BookingCardComponent, (prev, next) => {
  // Compare key props that actually affect rendering
  return (
    prev.booking.id === next.booking.id &&
    prev.booking.status === next.booking.status &&
    prev.booking.startTime === next.booking.startTime &&
    prev.hasReview === next.hasReview &&
    prev.isCancelling === next.isCancelling &&
    prev.showActions === next.showActions &&
    prev.variant === next.variant
  );
});
